import React from 'react';
import { type BookingRecord } from '../../services/graphql/bookingService';
import { type User } from '../../services/graphql/authService';
import BookingCard from '../BookingCard/BookingCard';
import ProgressDialog from '../ProgressDialog';

export type BookingsDisplayMode = 'list' | 'grid';
export type BookingCardMode = 'readonly' | 'admin' | 'customer' | 'serviceprovider';

export interface BookingsListLayoutProps {
  bookings: BookingRecord[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  currentUser: User | null;
  mode: BookingCardMode;
  displayMode?: BookingsDisplayMode;
  title: string;
  emptyMessage?: string;
  loadingMessage?: string;
  onConfirm?: (bookingId: string) => void;
  onCancel?: (bookingId: string, status: string, reason: string) => void;
  onEdit?: (bookingId: string) => void;
  className?: string;
  showRefreshButton?: boolean;
  onRefresh?: () => void;
  children?: React.ReactNode; // For additional content like filters
  'data-testid'?: string;
}

/**
 * Unified BookingsListLayout component for consistent display across all booking pages
 */
export const BookingsListLayout: React.FC<BookingsListLayoutProps> = ({
  bookings,
  loading,
  error,
  successMessage,
  currentUser,
  mode,
  displayMode = 'list',
  title,
  emptyMessage = 'No bookings found.',
  loadingMessage = 'Loading bookings...',
  onConfirm,
  onCancel,
  onEdit,
  className = '',
  showRefreshButton = false,
  onRefresh,
  children,
  'data-testid': dataTestId = 'bookings-list-layout'
}) => {
  
  // Transform booking data to match BookingCard props format
  const transformBookingData = (booking: BookingRecord) => ({
    ...booking,
    pickup: booking.pickup_location || '',
    destination: booking.dropoff_location || '',
    pickupType: booking.pickup_type || 'scheduled',
    pickupDate: booking.pickup_date || '',
    pickupTime: booking.pickup_time || '',
    companyName: booking.company_name || '',
    countryCode: booking.country_code || '',
    passengerName: booking.passenger_name || '',
    passengerMobile: booking.passenger_mobile || '',
    passengerEmail: booking.passenger_email || '',
    paymentMethod: booking.payment_method || '',
    communicationPrefs: booking.communication_prefs || { voice: false, sms: false, whatsapp: false, email: false },
    estimatedDuration: booking.estimated_duration ? { car: booking.estimated_duration, bus: booking.estimated_duration } : null,
    bookingNumber: booking.booking_number || '',
    bookingId: booking.id,
    selectedRide: booking.selected_ride || '',
    passengers: booking.passengers || 0,
    luggage: booking.luggage || 0,
    sendEmailCopy: false,
    emailCopyAddress: '',
    timezone: 'UTC',
    service_provider_name: booking.service_provider_name,
    created_at: booking.$createdAt || '',
  });

  // Sort bookings by created date descending (latest first)
  const sortedBookings = [...bookings].sort((a, b) => {
    const dateA = a.$createdAt ? new Date(a.$createdAt).getTime() : 0;
    const dateB = b.$createdAt ? new Date(b.$createdAt).getTime() : 0;
    return dateB - dateA;
  });

  // Get container CSS classes based on display mode
  const getContainerClasses = () => {
    const baseClasses = 'w-full';
    
    switch (displayMode) {
      case 'grid':
        return `${baseClasses} grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4`;
      case 'list':
      default:
        return `${baseClasses} flex flex-col gap-6`;
    }
  };

  // Loading state
  if (loading) {
    return (
      <ProgressDialog 
        isOpen={loading} 
        message={loadingMessage} 
        subMessage="Please wait while we fetch your data." 
      />
    );
  }

  return (
    <div className={`bookings-list-layout ${className}`} data-testid={dataTestId}>
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground" data-testid={`${dataTestId}-title`}>{title}</h1>
        {showRefreshButton && onRefresh && (
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
            disabled={loading}
            data-testid={`${dataTestId}-refresh-button`}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        )}
      </div>

      {/* Additional content (filters, etc.) */}
      {children}

      {/* Error Message */}
      {error && (
        <div className="p-4 mb-4 text-red-600 bg-red-50 rounded-lg flex items-center border border-red-200" data-testid={`${dataTestId}-error-message`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="p-4 mb-4 text-green-600 bg-green-50 rounded-lg flex items-center border border-green-200" data-testid={`${dataTestId}-success-message`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Bookings List/Grid */}
      {bookings.length === 0 ? (
        <div className="text-center text-foreground-muted py-8" data-testid={`${dataTestId}-empty-state`}>
          <p className="text-lg">{emptyMessage}</p>
        </div>
      ) : (
        <div className={getContainerClasses()} data-testid={`${dataTestId}-container`}>
          {displayMode === 'list' ? (
            // List layout with consistent spacing
            <ul className="space-y-6" data-testid={`${dataTestId}-list`}>
              {sortedBookings.map((booking) => (
                <li key={booking.id} data-testid={`${dataTestId}-item-${booking.id}`}>
                  <BookingCard
                    booking={transformBookingData(booking)}
                    mode={mode}
                    currentUser={currentUser}
                    onConfirm={onConfirm}
                    onCancel={onCancel}
                    onEdit={onEdit}
                    className="w-full"
                  />
                </li>
              ))}
            </ul>
          ) : (
            // Grid layout for admin pages
            sortedBookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={transformBookingData(booking)}
                mode={mode}
                currentUser={currentUser}
                onConfirm={onConfirm}
                onCancel={onCancel}
                onEdit={onEdit}
                className="w-full"
              />
            ))
          )}
        </div>
      )}

      {/* Booking Count Display */}
      {bookings.length > 0 && (
        <div className="mt-4 text-sm text-foreground-muted" data-testid={`${dataTestId}-count`}>
          Showing {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default BookingsListLayout;
